import { apiClient, type Paginated } from '@/core/api';
import type { DealStage } from './constants';
import type { Deal, DealInput, LostReasonInput } from './types';

export const BASE = '/crm/deals/';

export interface DealListParams {
  page?: number;
  search?: string;
  stage?: string;
  /** DRF `OrderingFilter` — e.g. `amount` or `-amount`. */
  ordering?: string;
  /** Server-side "my deals" filter; backend reads the requesting user. */
  mine?: boolean;
  /** Filter by deal owner (salesperson) — a User id. */
  owner?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** Created window (DateTime; `before` should be end-of-day). */
  created_after?: string;
  created_before?: string;
  /** Expected-close window (DateField) — e.g. "deals closing this month". */
  expected_close_date_after?: string;
  expected_close_date_before?: string;
  /** Filter to rows carrying this collab tag id. */
  tag?: number;
}

export async function listDeals(params: DealListParams = {}): Promise<Paginated<Deal>> {
  const { data } = await apiClient.get<Paginated<Deal>>(BASE, { params });
  return data;
}

export async function getDeal(id: string | number): Promise<Deal> {
  const { data } = await apiClient.get<Deal>(`${BASE}${id}/`);
  return data;
}

export async function createDeal(input: DealInput): Promise<Deal> {
  const { data } = await apiClient.post<Deal>(BASE, input);
  return data;
}

export async function updateDeal(id: string | number, input: Partial<DealInput>): Promise<Deal> {
  const { data } = await apiClient.patch<Deal>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a deal. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreDeal} to reverse.
 */
export async function archiveDeal(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreDeal(id: string | number): Promise<Deal> {
  const { data } = await apiClient.post<Deal>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived deal (hard delete; admin only, irreversible). */
export async function purgeDeal(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** Move a deal to a new stage — Kanban drag-drop / explicit change. Audited (C8).
 *  `lost` is required for `stage: 'lost'` (ADR-108). */
export async function setDealStage(
  id: string | number,
  stage: DealStage,
  lost?: LostReasonInput,
): Promise<Deal> {
  const { data } = await apiClient.post<Deal>(`${BASE}${id}/set-stage/`, { stage, ...lost });
  return data;
}
